import { Task, taskZod } from 'packages/lib/api/annotations/contract/task';
import { apiGetWithEtag, patch } from 'packages/lib/api/annotations/rest';
import retry from 'async-retry';
import { annotationStateMachine } from 'packages/task-man/service/state-machine';
import { z } from 'zod';

/**
 * This Task Management service method is used specifically for use-cases where users
 * pickup and self-assign individual tasks. It changes both the Task.annotationState
 * and the Task.assignee.
 *
 * If the given task is already assigned, or in an invalid annotationState, it will throw.
 *
 * Consumers of this method are responsible for fetching a different task in the case that
 * the provided task is already assigned or otherwise unable to be assigned.
 */
export const pickupTask = async (
  task: Task,
  userEmail: string
): Promise<Task> => {
  const apiPath = `/tasks/${task.token}`;
  let { taskLatest, etag, payload } = await incrementTaskState(task, userEmail);
  if (taskLatest.assignee) {
    throw new Error('This task is already assigned to another user');
  }
  /**
   * Note:
   *  - we retry the patch request if it fails due expired etag for the given task
   *  - but we don't try to fetch a different task, because that particular use-case
   *    is scoped to other methods that will re-use this method
   */
  await retry(
    async () => {
      try {
        return await patch(apiPath, payload, { 'If-Match': etag });
      } catch (err) {
        // re-fetch the latest version of the task one time and re-try
        ({ taskLatest, etag, payload } = await incrementTaskState(
          task,
          userEmail
        ));
        throw err;
      }
    },
    {
      retries: 1,
    }
  );
  /**
   * TODO: Integrate user task-state-changed metrics
   */
  return { ...taskLatest, ...payload };
};

/**
 * Private internal function for the pickupTask method.
 */
export async function incrementTaskState(
  task: Pick<Task, 'annotationState' | 'token'>,
  userEmail: string
): Promise<{
  taskLatest: Task;
  etag: string;
  payload: Pick<Task, 'assignee' | 'annotationState'>;
}> {
  const { data: taskLatest, etag } = await apiGetWithEtag(
    `/tasks/${task.token}`,
    taskZod.parse
  );
  if (!etag) {
    throw new Error(
      `Response for /tasks/${task.token} returned invalid or undefined ETag headers`
    );
  }
  const nextAnnotationState = annotationStateMachine(taskLatest).ASSIGN;
  if (!nextAnnotationState) {
    throw new Error(
      `Cannot perform action ASSIGN on Task with annotationState "${taskLatest.annotationState}"`
    );
  }
  const { success: isValidEmail } = z.string().email().safeParse(userEmail);
  if (!userEmail || !isValidEmail) {
    throw new Error(`Invalid or missing email address: ${userEmail}`);
  }
  return {
    taskLatest,
    etag,
    payload: {
      assignee: userEmail,
      annotationState: nextAnnotationState,
    },
  };
}
